import { useDuplicateMultipleSample } from "@/api/multiplesample";
import { zodResolver } from "@hookform/resolvers/zod";
import { isAxiosError } from "axios";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import DuplicateEntityDialog from "../../modal/DuplicateEntityDialog";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../ui/form";
import { Input } from "../../ui/input";

const schema = z.object({
  exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement: z
    .string()
    .min(1, "required"),
});

type FormType = z.infer<typeof schema>;

type DuplicateExemplaireDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sourceExemplaireId: string | undefined;
  onDuplicateSuccess: (created: {
    id: string;
    exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement?: string;
  }) => void;
};

const DuplicateExemplaireDialog = ({
  open,
  onOpenChange,
  sourceExemplaireId,
  onDuplicateSuccess,
}: DuplicateExemplaireDialogProps) => {
  const { t } = useTranslation();
  const form = useForm<FormType>({
    reValidateMode: "onSubmit",
    resolver: zodResolver(schema),
    defaultValues: {
      exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement: "",
    },
  });
  const { mutateAsync, isPending } = useDuplicateMultipleSample();

  const handleSubmit = async (data: FormType) => {
    if (!sourceExemplaireId) return;
    try {
      const created = await mutateAsync({
        sourceId: sourceExemplaireId,
        exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement:
          data.exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement,
      });
      onDuplicateSuccess({
        id: created.id,
        exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement:
          created.exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement,
      });
      onOpenChange(false);
      form.reset();
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 409) {
        form.setError(
          "exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement",
          {
            message: t("itemWithSameIdAlreadyExists"),
          },
        );
        return;
      }
      form.setError("root", { message: t("unknownError") });
      throw err;
    }
  };

  return (
    <DuplicateEntityDialog<FormType>
      open={open}
      onOpenChange={onOpenChange}
      title={t("duplicateExemplaire")}
      form={form}
      onSubmit={handleSubmit}
      isLoading={isPending}
    >
      <FormField
        control={form.control}
        name="exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement"
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              {t(
                "exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement",
              )}
            </FormLabel>
            <FormControl>
              <Input {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormMessage>{form.formState.errors.root?.message}</FormMessage>
    </DuplicateEntityDialog>
  );
};

export default DuplicateExemplaireDialog;
